import React from 'react'
import { Container, Grid, Typography, Box, Pagination, Stack, useMediaQuery, useTheme, Alert, Button } from '@mui/material'
import SearchBar from './components/SearchBar'
import ListingCard from './components/ListingCard'
import { HeaderAd, SidebarAd, InContentAd, FooterAd, MobileBannerAd } from './components/AdSense'
import ErrorBoundary from './components/ErrorBoundary'
import { Listing } from './types'
import { API_BASE_URL } from './lib/config'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
// No dummy data imports - only real backend data
import './i18n/i18n'

function App() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const [listings, setListings] = React.useState<Listing[]>([])
  const [totalPages, setTotalPages] = React.useState(1)
  const rawPage = Number(params.get('page') || 1)
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)
  const theme = useTheme()

  type SearchValue = { q: string; city: string; min?: number; max?: number; furnished: boolean }
  const readSearch = (): SearchValue => ({
    q: params.get('q') || '',
    city: params.get('city') || '',
    min: params.get('min') ? Number(params.get('min')) : undefined as number | undefined,
    max: params.get('max') ? Number(params.get('max')) : undefined as number | undefined,
    furnished: params.get('furnished') === 'true'
  })

  const [search, setSearch] = React.useState<SearchValue>(readSearch)
  React.useEffect(() => { setSearch(readSearch()) }, [params])

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const cities: string[] = []
  const syncParams = (next: SearchValue, nextPage?: number) => {
    const p = new URLSearchParams()
    if (next.city) p.set('city', next.city)
    if (next.q) p.set('q', next.q)
    if (next.min != null) p.set('min', String(next.min))
    if (next.max != null) p.set('max', String(next.max))
    if (next.furnished) p.set('furnished', 'true')
    p.set('page', String(nextPage ?? page))
    setParams(p, { replace: true })
  }
  const onSearch = () => syncParams(search, 1)
  const onReset = () => {
    const empty = { q: '', city: '', min: undefined, max: undefined, furnished: false }
    setSearch(empty)
    syncParams(empty, 1)
  }

  React.useEffect(() => {
    let isMounted = true
    const controller = new AbortController()
    const p = new URLSearchParams(params)
    p.set('page', String(page - 1))
    setLoading(true)
    setError(false)
    setListings([])

    // Fetch data from Spring Boot backend only
    fetch(`${API_BASE_URL}/wg/listings?${p.toString()}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON but got ${contentType}`)
        }

        return response.json()
      })
      .then(data => {
        if (isMounted) {
          setListings(data.content || []);
          setTotalPages(data.totalPages || 1)
        }
      })
      .catch((error) => {
        if (isMounted && error.name !== 'AbortError') {
          console.error('Failed to fetch listings from backend:', error)
          console.error('Backend URL:', `${API_BASE_URL}/wg/listings?${p.toString()}`)
          setError(true)
          setListings([])
          setTotalPages(1)
        }
      }).finally(() => {
        if (isMounted) setLoading(false)
      })
    return () => {
      isMounted = false
      controller.abort()
    }
  }, [params, page, attempt])

  return (
    <ErrorBoundary>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header Ad */}
        <Container maxWidth="lg" sx={{ py: 1 }}>
          <HeaderAd />
        </Container>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ flex: 1, py: 2 }}>
          <Box sx={{ mb: 3 }}>
            <SearchBar cities={cities} value={search} onChange={setSearch} onSubmit={onSearch} onReset={onReset} />
          </Box>

          {/* Mobile Banner Ad */}
          {isMobile && (
            <Box sx={{ mb: 2 }}>
              <MobileBannerAd />
            </Box>
          )}

          {loading && <Typography role="status">Angebote werden geladen…</Typography>}
          {error && <Alert severity="error" action={<Button onClick={() => setAttempt(value => value + 1)}>Erneut versuchen</Button>}>Angebote konnten nicht geladen werden.</Alert>}
          <Grid container spacing={3}>
            {/* Main Content */}
            <Grid item xs={12} lg={isMobile ? 12 : 9}>
              {loading || error ? null : listings.length > 0 ? (
                <Grid container spacing={2}>
                  {listings.map((l, index) => (
                    <React.Fragment key={l.id}>
                      <Grid item xs={12} md={6} lg={4}>
                        <ListingCard item={l} fallbackIndex={index} />
                      </Grid>
                      {/* In-content ads every 6 listings */}
                      {!isMobile && (index + 1) % 6 === 0 && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <InContentAd />
                          </Box>
                        </Grid>
                      )}
                    </React.Fragment>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h5" gutterBottom color="text.secondary">
                    {t('common.noResults')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Versuchen Sie andere Suchkriterien oder entfernen Sie Filter.
                  </Typography>
                </Box>
              )}

              <Stack direction="row" justifyContent="center" sx={{ my: 4 }}>
                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(_e, val) => { const next = new URLSearchParams(params); next.set('page', String(val)); setParams(next, { replace: true }) }}
                  color="primary"
                  size="large"
                />
              </Stack>
            </Grid>

            {/* Sidebar */}
            {!isMobile && (
              <Grid item xs={12} lg={3}>
                <Box sx={{ position: 'sticky', top: 20 }}>
                  <SidebarAd />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>

        {/* Footer Ad */}
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <FooterAd />
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={(theme) => ({
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.100',
            py: 3,
            mt: 'auto'
          })}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {t('header.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('footer.copyright')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('footer.about')}
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('footer.help')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('footer.contact')}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Rechtliches
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('footer.privacy')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('footer.terms')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('footer.imprint')}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </ErrorBoundary>
  )
}

export default App
